<div class="modal-container">
  <div mat-dialog-title>
    <div class="modal__header">
      <div class="modal__title">
        <h1>{{ title }}</h1>
      </div>
      <div class="modal-text mt-2">
        <span class="whitespace-pre-line">{{ instructions }}</span>
      </div>
      <button
        class="close-button"
        mat-dialog-close>
        <i class="material-symbols-rounded">close</i>
      </button>
    </div>
  </div>

  <mat-dialog-content aria-label="Add question modal">
    <div class="modal__body flex flex-col gap-8">
      <div class="flex flex-row gap-4 items-baseline">
        <scope-ui-input
          class="flex-1"
          inputClass="icon-field show-errors"
          [inputLabel]="questionTextLabel"
          [required]="true"
          [initialValue]="data.question?.displayText"
          (onInputChange)="question.displayText = $event" />
        <mat-slide-toggle
          *ngIf="!isFormula"
          class="scope-ui-toggle"
          [disableRipple]="true"
          [checked]="question.mandatory"
          (change)="question.mandatory = $event.checked">
          Required
        </mat-slide-toggle>
      </div>

      <div class="flex flex-row items-baseline justify-between">
        <mat-slide-toggle
          *ngIf="!isPrompt"
          class="scope-ui-toggle"
          [disableRipple]="true"
          [disabled]="isTrafficableToggleDisabled(question)"
          [checked]="question.trafficable"
          (change)="setTrafficable($event.checked)">
          Add to traffic data ({{ trafficableFieldsCount }}/{{ MAX_TRAFFICABLE_FIELDS }})
        </mat-slide-toggle>
        <mat-error *ngIf="isTrafficableToggleDisabled(question)">
          Limit of {{ MAX_TRAFFICABLE_FIELDS }} is reached
        </mat-error>
      </div>

      <mat-divider />

      <h4 class="product-heading-bold">{{ configureTitle }}</h4>

      <div class="flex flex-row gap-4 items-baseline">
        <scope-ui-input
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Enter Field Label"
          [initialValue]="data.question?.fieldLabel"
          (onInputChange)="setFieldLabel($event)" />
        <scope-ui-input
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Enter Field ID"
          [required]="true"
          [initialValue]="data.question?.fieldId"
          [control]="fieldIDControl"
          (onInputChange)="setFieldID($event)" />
      </div>

      <div
        *ngIf="isText"
        class="flex flex-row gap-4 items-baseline">
        <scope-ui-input
          type="number"
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Maximum Length"
          [initialValue]="data.question?.maxLength"
          [parseNumber]="true"
          [isInteger]="true"
          [minimum]="1"
          [maximum]="255"
          [maximumLength]="4"
          (onInputChange)="question.maxLength = $event" />
        <scope-ui-input
          type="text"
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Default Value"
          [control]="defaultValueControl"
          [maximumLength]="question.maxLength || 255"
          (onInputChange)="question.defaultValue = $event?.length ? $event : null" />
      </div>

      <div
        *ngIf="isNumber"
        class="flex flex-row gap-4 items-baseline">
        <mat-slide-toggle
          class="scope-ui-toggle flex-1"
          [disableRipple]="true"
          [checked]="question.wholeNumber"
          (change)="question.wholeNumber = $event.checked">
          Whole numbers only
        </mat-slide-toggle>
        <scope-ui-input
          type="text"
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Default Value"
          [decimalPlaces]="decimalPlaces"
          [allowNegativeNumbers]="true"
          [minimum]="question.minimum"
          [maximum]="question.maximum"
          [parseNumber]="true"
          [control]="defaultValueControl"
          [maximumLength]="MAX_NUMBER_LENGTH"
          (onInputChange)="question.defaultValue = $event" />
      </div>

      <div
        *ngIf="isNumber"
        class="flex flex-row gap-4 items-baseline">
        <scope-ui-input
          type="text"
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Minimum Value"
          [decimalPlaces]="decimalPlaces"
          [allowNegativeNumbers]="true"
          [maximum]="question.maximum"
          [parseNumber]="true"
          [control]="minimumControl"
          [maximumLength]="MAX_NUMBER_LENGTH"
          (onInputChange)="question.minimum = $event" />
        <scope-ui-input
          type="text"
          class="flex-1"
          inputClass="icon-field show-errors"
          inputLabel="Maximum Value"
          [decimalPlaces]="decimalPlaces"
          [allowNegativeNumbers]="true"
          [minimum]="question.minimum"
          [parseNumber]="true"
          [control]="maximumControl"
          [maximumLength]="MAX_NUMBER_LENGTH"
          (onInputChange)="question.maximum = $event" />
      </div>

      <div
        *ngIf="isDate"
        class="flex flex-row gap-4 items-baseline">
        <scope-ui-datepicker
          class="flex-1"
          inputLabel="Minimum Date"
          drops="up"
          [singleDatePicker]="true"
          [maxDate]="question.maxDate"
          [initialValue]="question?.minDate"
          (onSelect)="question.minDate = $event || null">
        </scope-ui-datepicker>
        <scope-ui-datepicker
          class="flex-1"
          inputLabel="Maximum Date"
          drops="up"
          [singleDatePicker]="true"
          [minDate]="question.minDate"
          [initialValue]="question?.maxDate"
          (onSelect)="question.maxDate = $event || null">
        </scope-ui-datepicker>
      </div>

      <formula-builder
        *ngIf="isFormula"
        [scenario]="scenario"
        [(value)]="question.formula"
        [required]="true"
        [control]="formulaControl"
        [dynamicFields]="data.dynamicFields" />

      <scope-ui-chips
        *ngIf="isDropdown"
        inputLabel="Options list"
        [selectedValue]="question.defaultValue"
        [control]="optionsListControl"
        [(values)]="options"
        (selectedValueChange)="question.defaultValue = $event"></scope-ui-chips>

      <mat-divider />

      <div class="flex gap-2 flex-col">
        <h4 class="product-heading-bold">Set a rule for when this question appears<span class="orange"> *</span></h4>
        <scope-ui-checkbox
          [title]="isFormula ? 'Always shown' : 'Always'"
          [disableUncheck]="true"
          [(ngModel)]="showAlways"
          (ngModelChange)="showAlways = true; question.hidden = false; showOnCondition = false" />
        <scope-ui-checkbox
          *ngIf="isFormula"
          title="Always hidden"
          [ngModel]="question.hidden"
          [disableUncheck]="true"
          matTooltip="Values still used downstream"
          matTooltipPosition="left"
          (ngModelChange)="question.hidden = true; showAlways = false; showOnCondition = false; checkDependencies()" />
        <scope-ui-checkbox
          [title]="isFormula ? 'Shown on condition' : 'On condition'"
          [disableUncheck]="true"
          [ngModel]="showOnCondition"
          matTooltip="Values used downstream only when condition met"
          matTooltipPosition="left"
          (ngModelChange)="showOnCondition = true; showAlways = false; question.hidden = false; checkDependencies()" />
        <formula-builder
          *ngIf="showOnCondition"
          title="Set a condition"
          [scenario]="scenario"
          [required]="true"
          [showResult]="false"
          [control]="conditionControl"
          [dynamicFields]="data.dynamicFields"
          [(value)]="question.displayCondition"
          (onFocus)="checkDependencies()" />
      </div>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions class="flex gap-2 justify-end">
    <button
      mat-button
      mat-dialog-close>
      <span class="tracking-normal">Cancel</span>
    </button>
    <button
      *ngIf="!(updateLoading$ | async)"
      mat-flat-button
      class="ml-8"
      (click)="submit()"
      [disabled]="disableSubmit()">
      <span class="tracking-normal">Save</span>
    </button>
    <button
      *ngIf="updateLoading$ | async"
      mat-flat-button
      class="ml-8">
      <img
        src="/assets/icons/loading.svg"
        alt="loading"
        class="icon-spinner" />
    </button>
  </mat-dialog-actions>
</div>
